// The output canvas stays full resolution; only masks and blur passes use smaller textures.
const VERTEX_SHADER = `#version 300 es
precision highp float;
out vec2 vUV;
void main() {
    vec2 position = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
    vUV = position;
    gl_Position = vec4(position * 2.0 - 1.0, 0.0, 1.0);
}
`;

const TEMPORAL_SHADER = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uIncoming;
uniform sampler2D uPrevious;
uniform float uWeight;
out vec4 outColor;
void main() {
    // ImageBitmaps and CPU mask arrays arrive top row first, unlike our video texture.
    float current = texture(uIncoming, vec2(vUV.x, 1.0 - vUV.y)).r;
    float previous = texture(uPrevious, vUV).r;
    outColor = vec4(mix(previous, current, uWeight), 0.0, 0.0, 1.0);
}
`;

const FOREGROUND_MASK = `
uniform sampler2D uSource;
uniform sampler2D uMask;
uniform bool uHasMask;

float foregroundAt(vec2 uv) {
    if (!uHasMask) return 1.0;
    return texture(uMask, uv).r;
}
`;

const BACKGROUND_SHADER = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
${FOREGROUND_MASK}
void main() {
    vec3 color = texture(uSource, vUV).rgb;
    float background = 1.0 - foregroundAt(vUV);
    // Blur weighted colors, so foreground colors do not bleed into the background.
    outColor = vec4(color * background, background);
}
`;

const BLUR_SHADER = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uInput;
uniform vec2 uStep;
out vec4 outColor;
void main() {
    vec4 sum = vec4(0.0);
    float weights = 0.0;
    for (int i = -6; i <= 6; i++) {
        float weight = exp(-float(i * i) / 18.0);
        sum += texture(uInput, vUV + float(i) * uStep) * weight;
        weights += weight;
    }
    outColor = sum / weights;
}
`;

const COMPOSITE_SHADER = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uBackground;
uniform bool uBlurEnabled;
out vec4 outColor;
${FOREGROUND_MASK}
void main() {
    vec3 original = texture(uSource, vUV).rgb;
    if (!uBlurEnabled) {
        outColor = vec4(original, 1.0);
        return;
    }
    float foreground = foregroundAt(vUV);
    vec4 background = texture(uBackground, vUV);
    vec3 blurred = background.a > 0.003
        ? background.rgb / background.a
        : original;
    outColor = vec4(mix(blurred, original, foreground), 1.0);
}
`;

const MASK_TIME_CONSTANT_MS = 50;

export class BlurRenderer {
	#canvas;
	#gl;
	#vao;
	#programs = [];
	#textures = [];
	#framebuffers = [];
	#temporal;
	#background;
	#blur;
	#composite;
	#source;
	#incoming;
	#maskTargets = [];
	#blurTargets = [];
	#historyIndex = 0;
	#hasMask = false;
	#lastMaskTimestamp = null;
	#maskWidth = 0;
	#maskHeight = 0;
	#incomingIsBitmap = false;
	#width = 0;
	#height = 0;

	constructor(canvas) {
		this.#canvas = canvas;
		this.#gl = canvas.getContext('webgl2', {
			alpha: false, antialias: false, depth: false, stencil: false,
			powerPreference: 'high-performance'
		});
		if (!this.#gl) throw new Error('Background blur requires WebGL2');

		try {
			const gl = this.#gl;
			this.#vao = gl.createVertexArray();
			this.#temporal = this.#CreateProgram(TEMPORAL_SHADER, ['uIncoming', 'uPrevious', 'uWeight']);
			const maskUniforms = ['uSource', 'uMask', 'uHasMask'];
			this.#background = this.#CreateProgram(BACKGROUND_SHADER, maskUniforms);
			this.#blur = this.#CreateProgram(BLUR_SHADER, ['uInput', 'uStep']);
			this.#composite = this.#CreateProgram(COMPOSITE_SHADER, [...maskUniforms, 'uBackground', 'uBlurEnabled']);
			this.#source = this.#CreateTexture();
			this.#incoming = this.#CreateTexture();
			gl.bindTexture(gl.TEXTURE_2D, this.#incoming);
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([0]));
			gl.disable(gl.BLEND);
			gl.disable(gl.DEPTH_TEST);
			gl.disable(gl.DITHER);
		} catch (error) {
			this.Dispose();
			throw error;
		}
	}

	#CreateProgram(fragmentSource, uniformNames) {
		const gl = this.#gl;
		const program = gl.createProgram();
		if (!program) throw new Error('Unable to create blur program');
		const shaders = [];
		try {
			for (const [type, source] of [[gl.VERTEX_SHADER, VERTEX_SHADER], [gl.FRAGMENT_SHADER, fragmentSource]]) {
				const shader = gl.createShader(type);
				if (!shader) throw new Error('Unable to create blur shader');
				shaders.push(shader);
				gl.shaderSource(shader, source);
				gl.compileShader(shader);
				if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
					throw new Error(`Blur shader compilation failed: ${gl.getShaderInfoLog(shader)}`);
				}
				gl.attachShader(program, shader);
			}
			gl.linkProgram(program);
			if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
				throw new Error(`Blur shader linking failed: ${gl.getProgramInfoLog(program)}`);
			}
			this.#programs.push(program);
			return {
				program,
				uniforms: Object.fromEntries(uniformNames.map(name => [name, gl.getUniformLocation(program, name)]))
			};
		} catch (error) {
			gl.deleteProgram(program);
			throw error;
		} finally {
			shaders.forEach(shader => gl.deleteShader(shader));
		}
	}

	#CreateTexture() {
		const gl = this.#gl;
		const texture = gl.createTexture();
		if (!texture) throw new Error('Unable to create blur texture');
		this.#textures.push(texture);
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		return texture;
	}

	#CreateFramebuffer() {
		const framebuffer = this.#gl.createFramebuffer();
		if (!framebuffer) throw new Error('Unable to create blur framebuffer');
		this.#framebuffers.push(framebuffer);
		return framebuffer;
	}

	#CreateTarget() {
		return {
			texture: this.#CreateTexture(),
			framebuffer: this.#CreateFramebuffer()
		};
	}

	#ResizeTarget(target, width, height, singleChannel = false) {
		const gl = this.#gl;
		gl.bindTexture(gl.TEXTURE_2D, target.texture);
		gl.texImage2D(gl.TEXTURE_2D, 0, singleChannel ? gl.R8 : gl.RGBA8,
			width, height, 0, singleChannel ? gl.RED : gl.RGBA, gl.UNSIGNED_BYTE, null);
		gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target.texture, 0);
		if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
			throw new Error('Unable to allocate blur framebuffer');
		}
	}

	#UsePass(pass, target, width, height) {
		const gl = this.#gl;
		gl.bindFramebuffer(gl.FRAMEBUFFER, target?.framebuffer ?? null);
		gl.viewport(0, 0, width, height);
		gl.useProgram(pass.program);
		gl.bindVertexArray(this.#vao);
	}

	#BindTexture(pass, uniform, texture, unit) {
		const gl = this.#gl;
		gl.activeTexture(gl.TEXTURE0 + unit);
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.uniform1i(pass.uniforms[uniform], unit);
	}

	#BindMask(pass) {
		const gl = this.#gl;
		this.#BindTexture(pass, 'uSource', this.#source, 0);
		this.#BindTexture(pass, 'uMask', this.#maskTargets[this.#historyIndex]?.texture ?? this.#incoming, 1);
		gl.uniform1i(pass.uniforms.uHasMask, this.#hasMask ? 1 : 0);
	}

	UpdateMask({ bitmap, data, width, height, timestamp }) {
		const gl = this.#gl;
		try {
			if (gl.isContextLost()) throw new Error('Blur WebGL context was lost');
			gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
			gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
			gl.activeTexture(gl.TEXTURE0);
			gl.bindTexture(gl.TEXTURE_2D, this.#incoming);
			const resize = width !== this.#maskWidth || height !== this.#maskHeight;
			const reallocate = resize || Boolean(bitmap) !== this.#incomingIsBitmap;
			if (bitmap && reallocate) {
				gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
			} else if (bitmap) {
				gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
			} else if (reallocate) {
				gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, data);
			} else {
				gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.RED, gl.UNSIGNED_BYTE, data);
			}
			this.#incomingIsBitmap = Boolean(bitmap);
			if (resize) {
				if (!this.#maskTargets.length) this.#maskTargets = [this.#CreateTarget(), this.#CreateTarget()];
				this.#maskTargets.forEach(target => this.#ResizeTarget(target, width, height, true));
				this.#maskWidth = width;
				this.#maskHeight = height;
				this.ResetMask();
			}

			const nextIndex = 1 - this.#historyIndex;
			const weight = this.#hasMask
				? 1 - Math.exp(-Math.max(0, timestamp - this.#lastMaskTimestamp) / MASK_TIME_CONSTANT_MS)
				: 1;
			this.#UsePass(this.#temporal, this.#maskTargets[nextIndex], width, height);
			this.#BindTexture(this.#temporal, 'uIncoming', this.#incoming, 0);
			this.#BindTexture(this.#temporal, 'uPrevious', this.#maskTargets[this.#historyIndex].texture, 1);
			gl.uniform1f(this.#temporal.uniforms.uWeight, weight);
			gl.drawArrays(gl.TRIANGLES, 0, 3);
			gl.bindFramebuffer(gl.FRAMEBUFFER, null);
			this.#historyIndex = nextIndex;
			this.#hasMask = true;
			this.#lastMaskTimestamp = timestamp;
		} finally {
			bitmap?.close();
		}
	}

	ResetMask() {
		this.#hasMask = false;
		this.#lastMaskTimestamp = null;
	}

	Render(video, blurAmount) {
		const gl = this.#gl;
		if (gl.isContextLost()) throw new Error('Blur WebGL context was lost');
		const width = video.videoWidth;
		const height = video.videoHeight;
		if (!width || !height) return;
		const blurWidth = Math.max(1, Math.ceil(width / 2));
		const blurHeight = Math.max(1, Math.ceil(height / 2));

		if (width !== this.#width || height !== this.#height) {
			this.#canvas.width = width;
			this.#canvas.height = height;
			gl.activeTexture(gl.TEXTURE0);
			gl.bindTexture(gl.TEXTURE_2D, this.#source);
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
			if (!this.#blurTargets.length) this.#blurTargets = [this.#CreateTarget(), this.#CreateTarget()];
			this.#blurTargets.forEach(target => this.#ResizeTarget(target, blurWidth, blurHeight));
			this.#width = width;
			this.#height = height;
			this.ResetMask();
		}

		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this.#source);
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
		gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, video);
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

		if (blurAmount > 0) {
			this.#UsePass(this.#background, this.#blurTargets[0], blurWidth, blurHeight);
			this.#BindMask(this.#background);
			gl.drawArrays(gl.TRIANGLES, 0, 3);

			this.#UsePass(this.#blur, this.#blurTargets[1], blurWidth, blurHeight);
			this.#BindTexture(this.#blur, 'uInput', this.#blurTargets[0].texture, 0);
			gl.uniform2f(this.#blur.uniforms.uStep, blurAmount / (6 * blurWidth), 0);
			gl.drawArrays(gl.TRIANGLES, 0, 3);

			this.#UsePass(this.#blur, this.#blurTargets[0], blurWidth, blurHeight);
			this.#BindTexture(this.#blur, 'uInput', this.#blurTargets[1].texture, 0);
			gl.uniform2f(this.#blur.uniforms.uStep, 0, blurAmount / (6 * blurHeight));
			gl.drawArrays(gl.TRIANGLES, 0, 3);
		}

		this.#UsePass(this.#composite, null, width, height);
		this.#BindMask(this.#composite);
		this.#BindTexture(this.#composite, 'uBackground', this.#blurTargets[0].texture, 2);
		gl.uniform1i(this.#composite.uniforms.uBlurEnabled, blurAmount > 0 ? 1 : 0);
		gl.drawArrays(gl.TRIANGLES, 0, 3);
	}

	Dispose() {
		const gl = this.#gl;
		if (!gl) return;
		gl.bindFramebuffer(gl.FRAMEBUFFER, null);
		gl.useProgram(null);
		gl.bindVertexArray(null);
		this.#framebuffers.forEach(framebuffer => gl.deleteFramebuffer(framebuffer));
		this.#textures.forEach(texture => gl.deleteTexture(texture));
		this.#programs.forEach(program => gl.deleteProgram(program));
		gl.deleteVertexArray(this.#vao);
		this.#framebuffers = [];
		this.#textures = [];
		this.#programs = [];
		this.#gl = null;
		this.#canvas = null;
	}
}
